import React, { useRef } from 'react';
import { Alert, Button, Card } from 'flowbite-react';
import { HiUpload } from 'react-icons/hi';

interface SelectImageProps {
  onImageSelected: (imageUrl: string) => void;
}

interface PresetImage {
  src: string;
  name: string;
}

const MAX_UPLOAD_SIZE_BYTES = 10 * 1024 * 1024;

const PRESET_IMAGES: PresetImage[] = [
  { src: 'blackworld400.png', name: 'World Map' },
  { src: 'triangle.jpg', name: 'Triangle' },
  { src: 'lion.png', name: 'Lion' },
];

const SelectImage: React.FC<SelectImageProps> = ({ onImageSelected }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please upload only image files');
      return;
    }

    if (file.size > MAX_UPLOAD_SIZE_BYTES) {
      alert('File size must be less than 10MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      onImageSelected(dataUrl);
    };
    reader.readAsDataURL(file);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="text-center mt-12 px-4">
      <h1 className="text-3xl font-bold mb-6 text-gray-900">Ropes - String Art Visualization</h1>
      <p className="text-lg mb-8 max-w-3xl mx-auto leading-relaxed text-gray-600">
        Choose an image to recreate as string art. You can upload your own image or select from our gallery.
      </p>

      <div className="mb-12">
        <h2 className="text-2xl font-semibold mb-6">Upload Custom Image</h2>
        <div className="text-center">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            className="hidden"
            id="image-upload"
          />
          <Button as="label" htmlFor="image-upload" color="green" size="xl">
            <HiUpload className="mr-2 h-6 w-6" />
            Upload Image
          </Button>
          <Alert color="info" className="mt-3 max-w-md mx-auto">
            <span className="font-medium">Supported formats:</span> JPG, PNG, GIF, etc. (Max 10MB)
          </Alert>
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-semibold mb-6">Gallery</h2>
        <div className="flex gap-8 justify-center flex-wrap">
          {PRESET_IMAGES.map((image) => (
            <Card
              key={image.src}
              className="max-w-sm cursor-pointer hover:shadow-lg transition-shadow duration-200"
              onClick={() => onImageSelected(image.src)}
              imgSrc={image.src}
              imgAlt={image.name}
            >
              <h5 className="text-lg font-semibold tracking-tight text-gray-900 dark:text-white">
                {image.name}
              </h5>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SelectImage;
